"use client";

import {toolRequestBody} from "./toolRequestBody";

import {useTranslation} from "react-i18next";
import {humanizeToolField} from "@repo/ui/gailgpt/tool-presentation";
import Icon, {ICON_SIZES} from "@/components/library/Icon";
import {NodeDefinitionIcon} from "@/components/workflows/utils/components/NodeDefinitionIcon";
import {getTranslatedNodeDefinitionLabel, NODE_DEFINITIONS} from "@/components/workflows/utils/helpers/workflow-node-registry";
import {omegaGraphSchema} from "@/services/wingman/omega-results";
import FrozenValue from "./FrozenValue";
import JsonDisclosure from "./JsonDisclosure";
import ToolPresentationDetails, {disclosureClass, type ToolDetailsProps} from "./ToolPresentationDetails";
import {occurrenceKeys, valueKey} from "./snapshotKeys";

/** Summarize the frozen publication request; never fetch a newer draft for an approval. */
export default function WorkflowToolDetails(props: ToolDetailsProps) {
   const {t} = useTranslation("ask");
   const {t: tWorkflow} = useTranslation("workflows");
   const {parameters} = props;
   const body = toolRequestBody(parameters);
   const parsed = omegaGraphSchema.safeParse(body.graph);
   if (!parsed.success || typeof body.name !== "string" || !body.name.trim()) return <ToolPresentationDetails {...props} />;
   const {nodes, edges} = parsed.data;
   const nameFor = (id: string) => nodes.find(node => node.id === id)?.name || id;

   return <div className="space-y-4 px-5 pb-4 pt-3">
      <div className="flex items-start gap-3">
         <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Icon icon="diagram-project" className="h-4 w-4" />
         </span>
         <div className="min-w-0">
            <h4 className="text-base font-semibold leading-snug tracking-tight text-900 [overflow-wrap:anywhere]">{body.name}</h4>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-500">
               {(typeof body.revision === "number" || typeof body.revision === "string") && <span>{t("workflowPanel.revision", {revision: body.revision})}</span>}
               <span>{t("workflowPanel.steps", {count: nodes.length})}</span>
               {body.isDryRun === true && <span className="font-medium text-primary">{t("toolApproval.workflow.dryRun")}</span>}
            </div>
         </div>
      </div>
      {typeof body.description === "string" && body.description && <p className="text-sm leading-relaxed text-600 [overflow-wrap:anywhere]">{body.description}</p>}
      <section aria-label={t("toolApproval.workflow.steps")}>
         <h5 className="mb-2 text-xs font-medium text-500">{t("toolApproval.workflow.steps")}</h5>
         <ul className="divide-y divide-neutral overflow-hidden rounded-lg border border-neutral">
            {occurrenceKeys(nodes, valueKey).map(({item: node, key}) => {
               const definition = NODE_DEFINITIONS.find(item => item.type === node.type);
               const typeLabel = definition ? getTranslatedNodeDefinitionLabel(node.type, tWorkflow) : humanizeToolField(node.type);
               return <li key={key}>
                  <details className="group/step">
                     <summary className={`${disclosureClass} gap-3 px-3 py-3`}>
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-100 text-500">
                           {definition ? <NodeDefinitionIcon node={definition} size={ICON_SIZES.XS} /> : <Icon icon="gear" className="h-3 w-3" />}
                        </span>
                        <span className="min-w-0 flex-1">
                           <span className="block text-sm font-medium leading-5 text-800 [overflow-wrap:anywhere]">{node.name || node.id}</span>
                           <span className="mt-0.5 block text-xs text-500 [overflow-wrap:anywhere]">{t(`toolApproval.workflow.nodeTypes.${node.type}`, {defaultValue: typeLabel})}</span>
                        </span>
                        <Icon icon="chevron-right" className="h-2.5 w-2.5 shrink-0 transition-transform group-open/step:rotate-90" />
                     </summary>
                     <div className="border-t border-neutral bg-100/30 p-3 text-sm text-700">
                        <FrozenValue value={Object.fromEntries(Object.entries(node).filter(([, value]) => value != null))} humanizeLabels />
                     </div>
                  </details>
               </li>;
            })}
         </ul>
      </section>
      <details className="group/connections">
         <summary className={disclosureClass}>
            <Icon icon="chevron-right" className="h-2.5 w-2.5 transition-transform group-open/connections:rotate-90" />
            {t("toolApproval.workflow.connections", {count: edges.length})}
         </summary>
         <ul className="mt-2 space-y-2 text-xs text-600">
            {occurrenceKeys(edges, valueKey).map(({item: edge, key}) => <li key={key} className="flex items-start gap-2 [overflow-wrap:anywhere]">
               <span className="min-w-0 flex-1">{nameFor(edge.from)}</span>
               <Icon icon="arrow-right" className="mt-0.5 h-3 w-3 shrink-0 text-400" />
               <span className="min-w-0 flex-1">{nameFor(edge.to)}</span>
            </li>)}
         </ul>
      </details>
      <div className="border-t border-neutral pt-3">
         <JsonDisclosure label={t("toolApproval.workflow.fullConfiguration")} value={parameters} />
      </div>
   </div>;
}
